/**
 * Hook unificado para validação de acesso do usuário
 * Combina validação de features e permissões
 * 
 * ATUALIZADO: Usa lógica unificada de resolveUserFeatures
 */

import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSessionContext } from '@/context/SessionContextProvider';
import { useStoreAuth } from '@/context/StoreAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { StoreFeature } from '@/types/networkUser';
import { StorePermission } from '@/types/shop';
import { 
  resolveUserFeatures, 
  resolveUserSubFeatures,
  hasFeatureInResult,
  hasSubFeatureInResult,
  type FeatureAccessResult,
  type SubFeatureAccessResult,
  type UserRole 
} from '@/utils/featureAccess';

/**
 * Tipo combinado para features e sub-features
 */
export type UserAccessResult = {
  features: FeatureAccessResult;
  subFeatures: SubFeatureAccessResult;
};

export const useUserAccess = () => {
  const { storeId, networkId, contextMode } = useSessionContext();
  const { hasPermission } = useStoreAuth();
  
  /**
   * Verifica se o usuário tem acesso a uma feature específica
   */
  const canAccessFeature = useCallback(async (feature: StoreFeature): Promise<boolean> => {
    try {
      if (!storeId || !networkId || contextMode !== 'STORE') {
        return false;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;
      
      const { data, error } = await supabase.rpc('has_feature_access', {
        _user_id: user.id,
        _store_id: storeId,
        _feature: feature as any
      });
      
      if (error) {
        console.error('Erro ao verificar acesso à feature:', error);
        return false;
      }
      
      return data || false;
    } catch (error) {
      console.error('Erro ao validar feature:', error);
      return false;
    }
  }, [storeId, networkId, contextMode]);
  
  /**
   * Verifica se o usuário tem acesso a uma página (feature + permissão)
   */
  const canAccessPage = useCallback(async (
    feature: StoreFeature,
    permission: StorePermission
  ): Promise<boolean> => {
    try {
      const hasFeature = await canAccessFeature(feature);
      const hasPerm = hasPermission(permission);
      
      return hasFeature && hasPerm;
    } catch (error) {
      console.error('Erro ao validar acesso à página:', error);
      return false;
    }
  }, [canAccessFeature, hasPermission]);

  /**
   * Query hook para features e sub-features do usuário no contexto atual
   * Usa lógica unificada de resolveUserFeatures e resolveUserSubFeatures
   */
  const useUserFeatures = () => {
    return useQuery<UserAccessResult>({
      queryKey: ['user-features-v3', networkId, storeId, contextMode],
      queryFn: async () => {
        if (!networkId) {
          return { 
            features: { mode: 'NONE' }, 
            subFeatures: { mode: 'NONE' } 
          };
        }
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          return { 
            features: { mode: 'NONE' }, 
            subFeatures: { mode: 'NONE' } 
          };
        }
        
        // Buscar todas as roles ativas do usuário na rede (incluindo sub_features)
        const { data: roles, error } = await supabase
          .from('user_network_roles')
          .select('role, features, sub_features, store_ids, scope_type')
          .eq('user_id', user.id)
          .eq('network_id', networkId)
          .eq('is_active', true);
        
        if (error) {
          console.error('Erro ao buscar features do usuário:', error);
          return { 
            features: { mode: 'NONE' }, 
            subFeatures: { mode: 'NONE' } 
          };
        }
        
        // console.log('🔍 useUserFeatures - roles encontradas:', roles?.length || 0);
        
        const typedRoles = (roles || []) as UserRole[];
        const mode = (contextMode || 'NETWORK') as 'NETWORK' | 'STORE';
        
        // Resolver features primeiro
        const featuresResult = resolveUserFeatures(typedRoles, storeId, mode);
        
        // Resolver sub-features usando o resultado de features como fallback
        const subFeaturesResult = resolveUserSubFeatures(typedRoles, storeId, mode, featuresResult);
        
        // console.log('🔍 useUserFeatures - resultado:', { features: featuresResult, subFeatures: subFeaturesResult });
        
        return {
          features: featuresResult,
          subFeatures: subFeaturesResult
        };
      },
      enabled: !!networkId,
      staleTime: 5 * 60 * 1000, // Cache por 5 minutos
      refetchOnWindowFocus: false,
    });
  };
  
  return {
    canAccessFeature,
    canAccessPage,
    useUserFeatures,
    // Exportar helpers para uso externo
    hasFeatureInResult,
    hasSubFeatureInResult,
  };
};
